/**
 * The shared context object. ARCHITECTURE.md §5.
 *
 * Created once, never reassigned, mutated in place. Passed to every system hook.
 * `src/core` owns the skeleton; each subsystem populates its own branch during `init`.
 */

import { Clock } from './time.js';
import { ConfigStore } from './config.js';
import { EventBus } from './events.js';
import { Logger } from './log.js';
import { MetricsRecorder } from './metrics.js';
import { PoolRegistry } from './pool.js';
import { SystemRegistry } from './system.js';
import { createRngStreams } from './rng.js';

export const DEFAULT_SEED = 0x5eed1207;

/**
 * @param {object} [options]
 * @param {number} [options.seed]
 * @param {boolean} [options.dev] enable dev overlays, event history, strict asserts
 * @param {boolean} [options.deterministic] wall-clock free stepping for tools
 * @param {boolean} [options.headless] no canvas/WebGL; combat + physics only
 * @param {HTMLCanvasElement|null} [options.canvas]
 * @param {object} [options.configOverrides] settings forced for this session (not persisted)
 * @param {string} [options.logLevel]
 */
export function createContext(options = {}) {
  const {
    seed = DEFAULT_SEED,
    dev = false,
    deterministic = false,
    headless = false,
    canvas = null,
    configOverrides = {},
    logLevel = dev ? 'debug' : 'warn',
  } = options;

  const metrics = new MetricsRecorder({ enabled: true });
  const events = new EventBus({ recordHistory: dev || deterministic });
  const log = new Logger(logLevel);
  events.onError = (name, err) => log.error(`event "${name}" handler failed`, err);
  // In deterministic runs a swallowed exception would silently corrupt a replay.
  events.strict = deterministic;

  const ctx = {
    version: '0.1.0',
    seed: seed >>> 0,
    config: new ConfigStore(configOverrides),
    flags: {
      dev,
      deterministic,
      headless,
      trainingMode: false,
      paused: false,
      /**
       * Capture mode: the page is being driven by `tools/` for a screenshot. Makes the renderer
       * preserve its drawing buffer so a frame rendered outside the rAF loop survives until the
       * compositor reads it.
       */
      capture: false,
      /** Set by tools to pin dynamic resolution and TAA jitter for reproducible captures. */
      pinnedQuality: deterministic,
    },

    time: new Clock({ deterministic }),
    rng: createRngStreams(seed),
    events,
    systems: new SystemRegistry(),
    pools: new PoolRegistry(metrics),
    metrics,
    log,

    /** Gameplay state. Written ONLY in fixedUpdate. Populated by src/combat during init. */
    sim: {
      /** @type {Array<object>} index 0 = P1, 1 = P2 */
      fighters: [],
      /** @type {object|null} */
      match: null,
      hitstop: 0,
      /** Pooled per-tick collision scratch, owned by src/physics. */
      collisions: null,
      /** Set by combat when a cinematic finisher owns the simulation. */
      cinematic: false,
    },

    /** Presentation state. Written ONLY in update/lateUpdate. Populated by src/render. */
    render: {
      canvas,
      width: 1920,
      height: 1080,
      dpr: 1,
      scene: null,
      camera: null,
      renderer: null,
      /** Presentation-only impact service, populated by ImpactFxSystem. */
      fx: null,
      exposure: 1.0,
      preset: 'high',
      /** Effective render scale after dynamic resolution. */
      renderScale: 1.0,
    },

    /** Input snapshots. Written by src/input in fixedUpdate before combat runs. */
    input: {
      /** @type {Array<object>} */
      players: [],
      bindings: null,
      recording: null,
      /** Which device drives each player slot: 'keyboard-left'|'keyboard-right'|'gamepad0'|... */
      assignments: ['keyboard-left', 'keyboard-right'],
    },

    /** Shared read-only registries populated during init (fighter defs, move tables). */
    data: {
      fighters: null,
      moves: null,
      animations: null,
      materials: null,
    },

    /** Coarse app state owned by src/ui; systems may read it. */
    app: {
      screen: 'boot', // boot | title | select | difficulty | fight | training | pause | results
      mode: 'vs-ai', // vs-ai | training
      selected: ['kestrel', 'bulwark'],
      aiDifficulty: 'skilled',
    },
  };

  return ctx;
}

/**
 * Resets the per-match parts of the context without rebuilding systems.
 * Systems that hold match-scoped state subscribe to `combat:round-start` instead of
 * relying on this, but tools use it to guarantee capture isolation.
 */
export function resetContextForMatch(ctx, seed = ctx.seed) {
  ctx.seed = seed >>> 0;
  const streams = createRngStreams(ctx.seed);
  ctx.rng.master = streams.master;
  ctx.rng.sim = streams.sim;
  ctx.rng.ai = streams.ai;
  ctx.rng.fx = streams.fx;
  ctx.rng.ui = streams.ui;
  ctx.time.reset();
  ctx.sim.hitstop = 0;
  ctx.sim.cinematic = false;
  ctx.pools.releaseAll();
  ctx.events.resetHistory();
  ctx.metrics.reset();
  return ctx;
}
